import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { timePunchAPI } from '../services/api';
import type { TimesheetPeriodSummary } from '../services/api';

const TimesheetHistory: React.FC = () => {
  const [periods, setPeriods] = useState<TimesheetPeriodSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    timePunchAPI.getTimesheetHistory()
      .then(res => {
        setPeriods(res.data);
        if (res.data.length > 0) {
          setExpandedId(res.data[0].period_id);
        }
      })
      .catch(err => console.error('Failed to load timesheet history:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id: number) => setExpandedId(prev => (prev === id ? null : id));

  const formatDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return <div className="container"><p>Loading timesheet history...</p></div>;

  if (periods.length === 0) {
    return (
      <div className="container">
        <h2>Timesheet History</h2>
        <p style={{ color: '#7f8c8d' }}>No timesheet records found.</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Timesheet History</h2>
        <Link to="/time-clock" className="btn btn-small btn-secondary">← Back to Time Clock</Link>
      </div>
      <p style={{ color: '#7f8c8d', marginBottom: '1rem' }}>
        View your hours for past pay periods. Click a period to see individual punches.
      </p>

      {periods.map(period => {
        const isExpanded = expandedId === period.period_id;
        const hasOT = parseFloat(period.ot_hours) > 0;
        const hasDT = parseFloat(period.dt_hours) > 0;
        const hasTimeoff = parseFloat(period.timeoff_hours) > 0;

        return (
          <div key={period.period_id} className="card" style={{ marginBottom: '0.75rem', padding: '1rem' }}>
            <div
              onClick={() => toggle(period.period_id)}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div>
                <strong>{period.period_name}</strong>
                <span style={{ marginLeft: '0.75rem', color: '#7f8c8d', fontSize: '0.9rem' }}>
                  {formatDate(period.start_date)} – {formatDate(period.end_date)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>{(parseFloat(period.total_hours) + parseFloat(period.timeoff_hours)).toFixed(2)} hrs</span>
                <span style={{ fontSize: '1.2rem', color: '#7f8c8d' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', flexWrap: 'wrap', fontSize: '0.9rem', color: '#555' }}>
              <span>Regular: {period.regular_hours}h</span>
              {hasOT && <span>Overtime: {period.ot_hours}h</span>}
              {hasDT && <span>Double Time: {period.dt_hours}h</span>}
              {hasTimeoff && <span>Time Off: {period.timeoff_hours}h</span>}
            </div>

            {isExpanded && (
              <div style={{ marginTop: '1rem' }}>
                {period.punches.length === 0 && (!period.time_off_entries || period.time_off_entries.length === 0) ? (
                  <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>No entries in this period.</p>
                ) : (
                  <table style={{ width: '100%', fontSize: '0.9rem' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Date</th>
                        <th style={{ textAlign: 'left' }}>Type</th>
                        <th style={{ textAlign: 'left' }}>Details</th>
                        <th style={{ textAlign: 'right' }}>Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ...period.punches.map(p => ({ kind: 'punch' as const, date: p.date, punch: p })),
                        ...(period.time_off_entries || []).map(e => ({ kind: 'timeoff' as const, date: e.date, timeOff: e })),
                      ]
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map(row => row.kind === 'punch' ? (
                          <tr key={`p-${row.punch.id}`}>
                            <td>{formatDate(row.punch.date)}</td>
                            <td>Punch</td>
                            <td>{formatTime(row.punch.clock_in)} – {row.punch.clock_out ? formatTime(row.punch.clock_out) : <em style={{ color: '#27ae60' }}>Active</em>}</td>
                            <td style={{ textAlign: 'right' }}>{row.punch.calculated_hours}</td>
                          </tr>
                        ) : (
                          <tr key={`to-${row.timeOff!.id}`} style={{ backgroundColor: '#f0f8ff' }}>
                            <td>{formatDate(row.timeOff!.date)}</td>
                            <td>
                              <span style={{
                                display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '10px',
                                fontSize: '0.8rem', color: 'white',
                                backgroundColor: row.timeOff!.time_off_type === 'sick' ? '#e74c3c' : row.timeOff!.time_off_type === 'vacation' ? '#3498db' : '#27ae60',
                              }}>
                                {row.timeOff!.time_off_type === 'sick' ? 'Sick' : row.timeOff!.time_off_type === 'vacation' ? 'Vacation' : 'Holiday'}
                              </span>
                            </td>
                            <td style={{ color: '#7f8c8d' }}>{row.timeOff!.notes || '—'}</td>
                            <td style={{ textAlign: 'right' }}>{row.timeOff!.hours}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TimesheetHistory;
